import React, { useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { TrendingUp } from 'lucide-react';
import {
  ResponsiveContainer, AreaChart, Area, Line, XAxis, YAxis, CartesianGrid,
  Tooltip as RechartsTooltip,
} from 'recharts';
import type { MonteCarloResult } from '@/hooks/useEmpMonteCarlo';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';

interface Props {
  history: MonteCarloResult[];
}

const fmtTick = (d: string) => {
  try { return format(new Date(d), 'dd/MM', { locale: ptBR }); } catch { return d; }
};

const CustomTooltip = ({ active, payload, label }: any) => {
  if (!active || !payload?.length) return null;
  const row = payload[0]?.payload;
  if (!row) return null;
  return (
    <div className="bg-popover border border-border rounded-lg p-3 shadow-md text-xs space-y-1">
      <p className="font-medium text-foreground">{fmtTick(label)}</p>
      <div className="flex justify-between gap-4">
        <span className="text-muted-foreground">P10</span>
        <span className="font-medium">{row.p10}d</span>
      </div>
      <div className="flex justify-between gap-4">
        <span className="text-muted-foreground">P50</span>
        <span className="font-medium">{row.p50}d</span>
      </div>
      <div className="flex justify-between gap-4">
        <span className="text-muted-foreground">P90</span>
        <span className="font-medium">{row.p90}d</span>
      </div>
      <div className="flex justify-between gap-4">
        <span className="text-muted-foreground">Prob. no prazo</span>
        <span className="font-medium">{row.prob}%</span>
      </div>
    </div>
  );
};

export const McTrendChart: React.FC<Props> = ({ history }) => {
  const chartData = useMemo(() =>
    [...history]
      .reverse()
      .map(mc => ({
        data: mc.created_at.slice(0, 10),
        p10: mc.p10_dias,
        p50: mc.p50_dias,
        p90: mc.p90_dias,
        prob: Math.round(mc.prob_on_time),
      })),
    [history]
  );

  if (chartData.length < 2) return null;

  return (
    <Card>
      <CardHeader className="pb-2">
        <div className="flex items-center gap-2">
          <TrendingUp className="h-4 w-4 text-primary" />
          <CardTitle className="text-base">Evolução da Faixa de Risco (MC)</CardTitle>
        </div>
      </CardHeader>
      <CardContent>
        <div className="h-[240px]">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={chartData} margin={{ top: 5, right: 10, left: 5, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" className="stroke-border" />
              <XAxis
                dataKey="data"
                tickFormatter={fmtTick}
                tick={{ fontSize: 12 }}
                className="fill-muted-foreground"
              />
              <YAxis
                tick={{ fontSize: 12 }}
                className="fill-muted-foreground"
                label={{ value: 'Dias', angle: -90, position: 'insideLeft', style: { fontSize: 12, fill: 'hsl(var(--muted-foreground))' } }}
              />
              <RechartsTooltip content={<CustomTooltip />} />

              {/* P10-P90 band */}
              <Area
                type="monotone"
                dataKey="p90"
                stackId="band"
                fill="hsl(var(--destructive) / 0.1)"
                stroke="none"
              />
              <Area
                type="monotone"
                dataKey="p10"
                stackId="band"
                fill="hsl(var(--background))"
                stroke="none"
              />

              {/* P50 line */}
              <Line
                type="monotone"
                dataKey="p50"
                stroke="hsl(var(--chart-mc-median))"
                strokeWidth={2}
                dot={{ r: 3 }}
              />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </CardContent>
    </Card>
  );
};
